import { ChartUpIcon, Link01Icon, RulerIcon, Settings05Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ParameterUnitsView } from '@/components/features/parameters/components/parameter-units/ParameterUnitsView';
import { AssetParametersTab } from '@/components/features/settings/assets/tabs/AssetParametersTab';
import { AssetParameterValuesTab } from '@/components/features/settings/assets/tabs/AssetParameterValuesTab';
import { ParametersTab } from '@/components/features/settings/assets/tabs/ParametersTab';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function ParameterManagementPageLoading() {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/parameter-management')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as string) || 'parameter-units',
    };
  },
  pendingComponent: ParameterManagementPageLoading,
  component: () => (
    <Suspense fallback={<ParameterManagementPageLoading />}>
      <ParameterManagementPage />
    </Suspense>
  ),
});

function ParameterManagementPage() {
  const { t } = useTranslation('parameter-management');
  const navigate = useNavigate();
  const { tab } = useSearch({ from: '/_authenticated/parameter-management' });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          {t('parameterManagement.title')}
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          {t('parameterManagement.description')}
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            to: '/parameter-management',
            search: (prev: any) => ({
              ...prev,
              tab: value,
            }),
          })
        }
        className="w-full"
      >
        <TabsList className="flex flex-wrap w-full overflow-x-auto">
          <TabsTrigger value="parameter-units" className="text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <HugeiconsIcon icon={RulerIcon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>{t('parameterManagement.tabs.parameterUnits')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="parameters" className="text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <HugeiconsIcon icon={Settings05Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>{t('parameterManagement.tabs.parameters')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="asset-parameters" className="text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <HugeiconsIcon icon={Link01Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>{t('parameterManagement.tabs.assetParameters')}</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="parameter-values" className="text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <HugeiconsIcon icon={ChartUpIcon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>{t('parameterManagement.tabs.parameterValues')}</span>
            </div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="parameter-units" className="mt-4">
          {tab === 'parameter-units' && <ParameterUnitsView />}
        </TabsContent>

        <TabsContent value="parameters" className="mt-4">
          {tab === 'parameters' && <ParametersTab />}
        </TabsContent>

        <TabsContent value="asset-parameters" className="mt-4">
          {tab === 'asset-parameters' && <AssetParametersTab />}
        </TabsContent>

        <TabsContent value="parameter-values" className="mt-4">
          {tab === 'parameter-values' && <AssetParameterValuesTab />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
